import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/utils/merge';
import useWindowSize from '@/utils/useWindowSize';
import { commApi } from './commApi';
import { useCommStore } from './commStore';
import { ConversationList } from './ConversationList';
import { ChatPane } from './ChatPane';
import { NewConversation } from './NewConversation';
import { STATUS_LABELS } from './format';

const PAGE = 30;

// List on the left, open conversation on the right (one at a time on a
// phone). `fixed` are filters set by the surrounding tab (e.g. role=shop);
// `extraFilters` are the Super Admin's own (city, user, unread). `compact`
// shows one pane at a time and fills its container (the shop's column);
// `onSelect` reports the open conversation to whoever hosts the inbox.
export const Inbox = ({
	fixed = {},
	extraFilters = {},
	cityId,
	initialId,
	showCity,
	compact,
	onSelect,
}) => {
	const me = useCommStore(s => s.me);
	const version = useCommStore(s => s.version);
	const { width } = useWindowSize({ full: true });
	const narrow = compact || width < 900;

	const [status, setStatus] = useState('');
	const [items, setItems] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(false);
	const [selected, setSelectedState] = useState(initialId || null);
	const setSelected = id => {
		setSelectedState(id);
		onSelect?.(id);
	};
	const [creating, setCreating] = useState(false);

	const filters = { ...fixed, ...extraFilters, status: status || undefined };
	const key = JSON.stringify(filters);

	// First page: on open, when the filters change, and on every real-time update.
	useEffect(() => {
		let alive = true;
		commApi.conversations({ ...JSON.parse(key), limit: PAGE, offset: 0 }).then(res => {
			if (!alive || res?.error) return;
			setItems(res.conversations);
			setHasMore(res.hasMore);
		});
		return () => {
			alive = false;
		};
	}, [key, version]);

	// Further pages, on scrolling down.
	const loadMore = useCallback(async () => {
		setLoading(true);
		const res = await commApi.conversations({
			...JSON.parse(key),
			limit: PAGE,
			offset: items.length,
		});
		setLoading(false);
		if (res?.error) return;
		setItems(prev => [...prev, ...res.conversations]);
		setHasMore(res.hasMore);
	}, [key, items.length]);

	const reload = async () => {
		const res = await commApi.conversations({ ...JSON.parse(key), limit: PAGE, offset: 0 });
		if (res?.error) return;
		setItems(res.conversations);
		setHasMore(res.hasMore);
	};

	const showList = !narrow || !selected;
	const showChat = !narrow || !!selected;

	return (
		<div
			className={cn(
				'flex overflow-hidden bg-white',
				compact
					? 'min-h-0 w-full flex-1'
					: 'h-[calc(100dvh-170px)] min-h-[420px] rounded-xl border border-slate-200',
			)}
		>
			{showList && (
				<div
					className={cn(
						'flex min-h-0 flex-col border-r border-slate-200',
						narrow ? 'w-full' : 'w-[360px] shrink-0',
					)}
				>
					<div className='flex items-center gap-2 border-b border-slate-200 p-2'>
						<select
							className='h-9 rounded-md border border-slate-300 px-2 text-[13px]'
							value={status}
							onChange={e => setStatus(e.target.value)}
							aria-label='Κατάσταση'
						>
							<option value=''>Ανοιχτές & κλειστές</option>
							{Object.entries(STATUS_LABELS).map(([k, v]) => (
								<option key={k} value={k}>
									{v}
								</option>
							))}
						</select>
						{!!me?.canStart?.length && (
							<button
								type='button'
								onClick={() => setCreating(true)}
								className='ml-auto flex shrink-0 items-center gap-1 rounded-md bg-[#0f9b9d] px-3 py-1.5 text-[13px] text-white'
							>
								<Plus size={16} /> Νέα
							</button>
						)}
					</div>
					<ConversationList
						items={items}
						selectedId={selected}
						onSelect={setSelected}
						onMore={loadMore}
						hasMore={hasMore}
						loading={loading}
						showCity={showCity}
					/>
				</div>
			)}
			{showChat &&
				(selected ? (
					<ChatPane
						key={selected}
						conversationId={selected}
						compact={compact}
						onBack={narrow ? () => setSelected(null) : undefined}
					/>
				) : (
					<div className='flex flex-1 items-center justify-center text-[14px] text-slate-400'>
						Επιλέξτε συνομιλία
					</div>
				))}
			{creating && (
				<NewConversation
					me={me}
					cityId={cityId}
					onClose={() => setCreating(false)}
					onStarted={id => {
						setCreating(false);
						setSelected(id);
						reload();
					}}
				/>
			)}
		</div>
	);
};
